import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Servicio para gestión de conciliaciones bancarias
 * Sigue el patrón establecido en el sistema financiero
 */
@Injectable({
  providedIn: 'root'
})
export class BankReconciliationService {
  private readonly apiUrl = `${environment.apiUrl}/BankReconciliation`;

  constructor(private http: HttpClient) { }

  // === CREACIÓN Y GESTIÓN DE CONCILIACIONES ===

  /**
   * Crea una nueva conciliación bancaria
   */
  createReconciliation(dto: BankReconciliationRequestDTO): Observable<any> {
    return this.http.post<any>(this.apiUrl, dto);
  }

  /**
   * Completa una conciliación en progreso
   */
  completeReconciliation(reconciliationId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${reconciliationId}/complete`, {});
  }

  /**
   * Cancela una conciliación en progreso
   */
  cancelReconciliation(reconciliationId: number, cancellationReason: string): Observable<any> {
    const body = { cancellationReason };
    return this.http.post<any>(`${this.apiUrl}/${reconciliationId}/cancel`, body);
  }

  // === CONSULTA DE COMPROBANTES PENDIENTES ===

  /**
   * Obtiene comprobantes de ingreso pendientes de conciliar
   */
  getPendingIncomeReceipts(params: PendingReceiptsParams): Observable<any> {
    let httpParams = new HttpParams()
      .set('bankId', params.bankId.toString())
      .set('month', params.month.toString())
      .set('year', params.year.toString());

    if (params.projectId) httpParams = httpParams.set('projectId', params.projectId.toString());
    if (params.participantId) httpParams = httpParams.set('participantId', params.participantId.toString());
    if (params.companyId) httpParams = httpParams.set('companyId', params.companyId.toString());
    if (params.minAmount) httpParams = httpParams.set('minAmount', params.minAmount.toString());
    if (params.maxAmount) httpParams = httpParams.set('maxAmount', params.maxAmount.toString());

    return this.http.get<any>(`${this.apiUrl}/pending-income-receipts`, { params: httpParams });
  }

  /**
   * Obtiene comprobantes de egreso pendientes de conciliar
   */
  getPendingExpenseVouchers(params: PendingReceiptsParams): Observable<any> {
    let httpParams = new HttpParams()
      .set('bankId', params.bankId.toString())
      .set('month', params.month.toString())
      .set('year', params.year.toString());

    if (params.minAmount) httpParams = httpParams.set('minAmount', params.minAmount.toString());
    if (params.maxAmount) httpParams = httpParams.set('maxAmount', params.maxAmount.toString());

    return this.http.get<any>(`${this.apiUrl}/pending-expense-vouchers`, { params: httpParams });
  }

  /**
   * Obtiene todos los comprobantes pendientes (ingresos y egresos)
   */
  getAllPendingReceipts(params: PendingReceiptsParams): Observable<any> {
    let httpParams = new HttpParams()
      .set('bankId', params.bankId.toString())
      .set('month', params.month.toString())
      .set('year', params.year.toString());

    if (params.projectId) httpParams = httpParams.set('projectId', params.projectId.toString());
    if (params.participantId) httpParams = httpParams.set('participantId', params.participantId.toString());
    if (params.companyId) httpParams = httpParams.set('companyId', params.companyId.toString());

    return this.http.get<any>(`${this.apiUrl}/pending-receipts`, { params: httpParams });
  }

  // === CONCILIACIÓN AUTOMÁTICA ===

  /**
   * Realiza conciliación automática por coincidencia de montos
   */
  autoReconcileByAmount(reconciliationId: number, tolerance: number = 0.01): Observable<any> {
    const params = new HttpParams().set('tolerance', tolerance.toString());
    return this.http.post<any>(`${this.apiUrl}/${reconciliationId}/auto-reconcile`, {}, { params });
  }

  /**
   * Realiza conciliación automática por rango de fechas
   */
  autoReconcileByDateRange(reconciliationId: number, dateRangeDays: number = 3, amountTolerance: number = 0.01): Observable<any> {
    const params = new HttpParams()
      .set('dateRangeDays', dateRangeDays.toString())
      .set('amountTolerance', amountTolerance.toString());
    return this.http.post<any>(`${this.apiUrl}/${reconciliationId}/auto-reconcile-by-date`, {}, { params });
  }

  // === CONCILIACIÓN MANUAL ===

  /**
   * Concilia manualmente un comprobante específico
   */
  manualReconcileDetail(detailId: number, request: ManualReconcileRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/detail/${detailId}/reconcile`, request);
  }

  /**
   * Deshace la conciliación de un comprobante específico
   */
  undoReconciliation(detailId: number, reason: string): Observable<any> {
    const body = { reason };
    return this.http.post<any>(`${this.apiUrl}/detail/${detailId}/undo`, body);
  }

  // === CONSULTAS Y REPORTES ===

  /**
   * Obtiene el resumen de una conciliación específica
   */
  getReconciliationSummary(reconciliationId: number, includeDetails: boolean = false): Observable<any> {
    const params = new HttpParams().set('includeDetails', includeDetails.toString());
    return this.http.get<any>(`${this.apiUrl}/${reconciliationId}`, { params });
  }

  /**
   * Obtiene el historial de conciliaciones con paginación
   */
  getReconciliationHistory(params: ReconciliationHistoryParams): Observable<any> {
    let httpParams = new HttpParams()
      .set('pageNumber', params.pageNumber.toString())
      .set('pageSize', params.pageSize.toString());

    if (params.bankId) httpParams = httpParams.set('bankId', params.bankId.toString());
    if (params.status) httpParams = httpParams.set('status', params.status);

    return this.http.get<any>(this.apiUrl, { params: httpParams });
  }

  /**
   * Obtiene estadísticas de conciliaciones
   */
  getReconciliationStats(bankId?: number, periodFrom?: string, periodTo?: string): Observable<any> {
    let params = new HttpParams();
    
    if (bankId) params = params.set('bankId', bankId.toString());
    if (periodFrom) params = params.set('periodFrom', periodFrom);
    if (periodTo) params = params.set('periodTo', periodTo);

    return this.http.get<any>(`${this.apiUrl}/stats`, { params });
  }

  /**
   * Genera reporte de conciliación en Excel
   */
  generateReconciliationReport(reconciliationId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${reconciliationId}/report`, { 
      responseType: 'blob',
      headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    });
  }

  /**
   * Valida si se puede crear una conciliación para el período especificado
   */
  validateReconciliationPeriod(bankId: number, periodFrom: string, periodTo: string): Observable<any> {
    const params = new HttpParams()
      .set('bankId', bankId.toString())
      .set('periodFrom', periodFrom)
      .set('periodTo', periodTo);

    return this.http.get<any>(`${this.apiUrl}/validate-period`, { params });
  }

  // === MÉTODOS AUXILIARES ===

  /**
   * Descarga un archivo de reporte
   */
  downloadReport(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}

// === INTERFACES DE TIPOS ===

export interface BankReconciliationRequestDTO {
  bankId: number;
  reconciliationType: 'INCOME' | 'EXPENSE' | 'BOTH';
  month: number;
  year: number;
  totalBankAmount: number;
  notes?: string;
  details: BankReconciliationDetailRequestDTO[];
  filters?: BankReconciliationFiltersDTO;
}

export interface BankReconciliationDetailRequestDTO {
  receiptType: 'INCOME' | 'EXPENSE';
  incomeReceiptId?: number;
  expenseVoucherId?: number;
  bankAmount: number;
  adjustmentReason?: string;
  notes?: string;
  incomeFees?: any[];
  expenseFees?: any[];
}

export interface BankReconciliationFiltersDTO {
  projectId?: number;
  participantId?: number;
  companyId?: number;
  onlyUnreconciled?: boolean;
  minAmount?: number;
  maxAmount?: number;
  autoReconciliationTolerance?: number;
}

export interface PendingReceiptsParams {
  bankId: number;
  month: number;
  year: number;
  projectId?: number;
  participantId?: number;
  companyId?: number;
  minAmount?: number;
  maxAmount?: number;
}

export interface ReconciliationHistoryParams {
  bankId?: number;
  status?: string;
  pageNumber: number;
  pageSize: number;
}

export interface ManualReconcileRequest {
  reconciledAmount: number;
  adjustmentReason?: string;
  notes?: string;
}

// === ENUMS ===
export enum BankReconciliationType {
  INCOME = 'INCOME',
  EXPENSE = 'EXPENSE',
  BOTH = 'BOTH'
}

export enum BankReconciliationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum BankReceiptType {
  INCOME = 'INCOME',
  EXPENSE = 'EXPENSE'
}
